import loadable from '@loadable/component';
import queryString from 'query-string-for-all';
import { updateProductCollectionFilters } from './actions';
import productCollectionPageQuery from './query.graphql';
import globalFilterSearch from './search-global-filters.graphql';
import productCollectionSearchQuery from './search.graphql';
import { getSortKeyForProperty } from './utils/properties';

let store;
if (IS_BROWSER) {
  // We need to do this here because we need access to the store in updateFilters
  // However we can't statically import it since produces with SSR. This way, we only import it in the browser.
  // eslint-disable-next-line global-require
  store = require('src/client/store').default;
}

const ProductCollection = loadable(() =>
  import(/* webpackChunkName: "product-collection" */ './product-collection')
);

ProductCollection.graphQL = productCollectionPageQuery;

ProductCollection.loadOrder = [{ main: true }];

const mergeIntoQueryArray = (arr, obj, prop) => {
  const currentObj = arr.find(item => item[prop] === obj[prop]);
  if (currentObj) {
    return arr.map(item =>
      item[prop] === obj[prop]
        ? {
            ...item,
            ...obj,
          }
        : item
    );
  }
  return arr.concat(obj);
};

const fetches = {};

const elasticRequest = async (query, variables, graphql) => {
  // Avoid race conditions when data arrives in different order
  // by setting a current id
  const current = Math.random();
  fetches[variables.aggregationId] = current;
  try {
    const res = await graphql(query, variables);

    if (res.errors) {
      return null;
    }

    // A later fetch is running, do not merge data
    if (fetches[variables.aggregationId] !== current) {
      return null;
    }
    return res;
  } catch (e) {
    console.error(e);
    return null;
  }
};
const refreshAggregation = (variables, action) => async ({
  dispatch,
  graphql,
}) => {
  if (action) {
    dispatch(action, { fetch: 'none' });
  }
  const res = await elasticRequest(
    productCollectionSearchQuery,
    variables,
    graphql
  );
  if (!res) {
    return;
  }
  dispatch(
    {
      type: 'AGGREGATION_RECEIVE',
      id: variables.aggregationId,
      data: [res.data.productCollectionAggregation],
    },
    { dataReducer: true }
  );
};

const fetchGloballyFilteredAggregations = (variables, action) => async ({
  dispatch,
  graphql,
}) => {
  if (action) {
    dispatch(action, { fetch: 'none' });
  }
  const res = await elasticRequest(globalFilterSearch, variables, graphql);
  if (!res) {
    return;
  }
  dispatch(
    {
      type: 'AGGREGATION_RECEIVE',
      id: variables.aggregationId,
      data: res.data.globalFilterSearch,
    },
    { dataReducer: true }
  );
};

const replaceOrAdd = (array, entry, matchBy = 'property') => {
  const index = array.findIndex(x => x[matchBy] === entry[matchBy]);
  const newArr = [...array];
  if (index >= 0) {
    newArr.splice(index, 1, entry);
  } else {
    newArr.push(entry);
  }

  return newArr;
};

const updateFilters = (state, filters) => {
  const newState = {
    ...state,
    // Reset offset whenever filters are updated
    offset: 0,
    filters,
  };

  store.dispatch(updateProductCollectionFilters(state.id, filters));

  return newState;
};

ProductCollection.dataReducer = (action, data) => {
  switch (action.type) {
    case 'AGGREGATION_RECEIVE': {
      const slice = data.productCollection.slices.find(x => x.id === 'filter');
      if (!slice) {
        return null;
      }

      // There is a memo on the sections that we need to bail out from
      slice.sections = [].concat(
        slice.sections.map(section => {
          const sectionUpdate = action.data.find(({ id }) => section.id === id);
          if (!sectionUpdate) {
            return section;
          }

          return {
            ...section,
            ...sectionUpdate,
          };
        })
      );

      return {
        ...data,
      };
    }
    default: {
      return data;
    }
  }
};

const onlyValidFilters = filter => {
  switch (filter.type) {
    case 'term':
      return filter.id;
    case 'terms':
      return filter.ids && filter.ids.length;
    default:
      return true;
  }
};

ProductCollection.reducer = (state = { aggregations: [] }, action) => {
  switch (action.type) {
    case 'SSR':
    case 'URL_CHANGE': {
      const { offset, sort, direction } = queryString.parse(
        action.location.search
      );
      return {
        ...state,
        offset: parseInt(offset, 10) || 0,
        sort,
        direction,
        // Use url to construct new filters server side
        url: action.location.pathname + action.location.search,
        filters: [],
      };
    }
    case 'ROUTE_LOADED': {
      const productCollection = action.data?.productCollection;
      const productsSlice = productCollection?.slices?.find(
        slice => slice.__typename === 'ProductsSlice'
      );

      return {
        ...state,
        id: productCollection?.id,
        // Clear url and use local filters instead
        url: null,
        filters: productCollection?.filters,
        propertyColumnIds: productsSlice?.propertyColumns?.map(({ id }) => id),
      };
    }
    case 'FILTER_RANGE_UPDATE': {
      return updateFilters(state, replaceOrAdd(state.filters, action.filter));
    }
    case 'FILTER_RANGE_GROUP_UPDATE': {
      let key;
      if (action.multiple) {
        const currentFilter = state.filters.find(
          x => x.property === action.property
        ) || { key: [] };
        const currentKeys = Array.isArray(currentFilter.key)
          ? currentFilter.key
          : [currentFilter.key];
        const index = currentKeys.indexOf(action.id);
        if (index >= 0) {
          currentKeys.splice(index, 1);
        } else {
          currentKeys.push(action.id);
        }
        key = currentKeys;
      } else {
        key = [action.id];
      }
      const filter = {
        type: 'range-group',
        property: action.property,
        key,
      };

      return updateFilters(
        state,
        !filter.key.length
          ? state.filters.filter(({ property }) => property !== action.property)
          : replaceOrAdd(state.filters, filter)
      );
    }
    case 'FILTER_BOOLEAN_UPDATE': {
      const filter = {
        type: 'boolean',
        id: action.id,
        property: action.property,
      };
      const newFilters = replaceOrAdd(state.filters, filter);
      return updateFilters(state, newFilters.filter(onlyValidFilters));
    }
    case 'FILTER_LIST_UPDATE': {
      const { id, clearCurrentFilters } = action;
      const filters = clearCurrentFilters ? [] : state.filters;
      const currentFilter = filters.find(x => x.property === action.property);

      const ids = [].concat(currentFilter?.ids || [], currentFilter?.id || []);

      if (!id) {
        return state;
      }

      if (!action.multiple) {
        const newFilters = replaceOrAdd(filters, {
          type: 'term',
          id,
          property: action.property,
        });

        return updateFilters(state, newFilters.filter(onlyValidFilters));
      }

      const index = ids.indexOf(id);
      if (index >= 0) {
        ids.splice(index, 1);
      } else {
        ids.push(id);
      }

      const filter =
        ids.length === 1
          ? {
              type: 'term',
              property: action.property,
              id: ids[0],
            }
          : {
              type: 'terms',
              property: action.property,
              ids,
            };

      const newFilters = replaceOrAdd(filters, filter);

      return updateFilters(state, newFilters.filter(onlyValidFilters));
    }
    case 'FILTER_LIST_TREE_UPDATE': {
      const otherActiveFilters = state.filters.filter(
        filter => filter.property !== action.property
      );

      const ids = action.ids;

      const filter =
        ids.length === 1
          ? {
              type: 'term',
              property: action.property,
              id: ids[0],
            }
          : {
              type: 'terms',
              property: action.property,
              ids,
            };

      const filters = ids.length
        ? [...otherActiveFilters, filter]
        : otherActiveFilters;
      return updateFilters(state, filters);
    }
    case 'FILTER_NAME_SEARCH_UPDATE': {
      const newFilters = state.filters.filter(f => f.type !== 'name-search');
      if (action.query?.length) {
        newFilters.push({
          type: 'name-search',
          query: action.query,
        });
      }
      return updateFilters(state, newFilters.filter(onlyValidFilters));
    }
    case 'FILTER_REMOVE': {
      // If a property is passed, clear filters for that property, otherwise all filters
      return action.property
        ? updateFilters(
            state,
            state.filters.filter(filter => filter.property !== action.property)
          )
        : updateFilters(state, []);
    }
    case 'AGGREGATION_CHANGED': {
      return {
        ...state,
        aggregations: mergeIntoQueryArray(
          state.aggregations,
          action.aggregation,
          'id'
        ),
      };
    }
    case 'AGGREGATION_SEARCH':
    case 'AGGREGATION_MORE_ITEMS': {
      const newAggregation = {
        ...state.aggregations.find(agg => agg.id === action.id),
        id: action.id,
      };

      if (action.query !== undefined) {
        newAggregation.query = action.query;
      }

      if (action.size) {
        newAggregation.size = action.size;
      }

      const variables = {
        id: state.id,
        aggregationId: action.id,
        query: {
          filters: state.filters,
          aggregations: [newAggregation],
        },
      };

      return refreshAggregation(variables, {
        type: 'AGGREGATION_CHANGED',
        aggregation: newAggregation,
      });
    }
    case 'GLOBAL_AGGREGATION_SEARCH': {
      return fetchGloballyFilteredAggregations(
        {
          definitionId: state.id,
          query: {
            filters: state.filters,
            globalPropertySearch: action.globalPropertySearch,
          },
        },
        {
          type: 'SET_QUERY',
          query: {
            // remove all existing aggregation state
            aggregations: [],
            globalPropertySearch: action.query,
          },
        }
      );
    }
    case 'SET_QUERY': {
      return {
        ...state,
        ...action.query,
      };
    }
    case 'ADD_PROPERTY_COLUMN': {
      return {
        ...state,
        propertyColumnIds: state.propertyColumnIds.concat([action.propertyId]),
      };
    }
    case 'REMOVE_PROPERTY_COLUMN': {
      const propertyColumnIds = state.propertyColumnIds.filter(
        property => property !== action.propertyId
      );

      // If we're currently sorting on the column we're removing, clear the sort method as well
      if (state.sort === getSortKeyForProperty(action.propertyId)) {
        return { ...state, propertyColumnIds, sort: undefined };
      }

      return { ...state, propertyColumnIds };
    }
    case 'ROUTE_DATA_REFETCHED': {
      return ({ history }) => {
        if (action.data?.productCollection?.url) {
          const { offset, sort, direction } = state;
          const { url, query } = queryString.parseUrl(
            action.data.productCollection.url
          );
          if (offset > 0) {
            query.offset = offset;
          }
          if (sort) {
            query.sort = sort;
          }
          if (direction) {
            query.direction = direction;
          }
          const queryStr = Object.keys(query).length
            ? `?${queryString.stringify(query)}`
            : '';
          history.push(`${url}${queryStr}`, {
            refreshRoute: false,
          });
        }
      };
    }
    default: {
      return state;
    }
  }
};

ProductCollection.variables = ({ id, slug, productPropertyIds }) => ({
  id,
  slug,
  productPropertyIds,
});
const SIMULATED_PROPERTY_COLUMNS = ['brand'];
ProductCollection.stateToVariables = state => {
  return {
    query: {
      url: state.url,
      filters: !state.url && state.filters ? state.filters : undefined,
      aggregations: state.aggregations,
      sort: state.sort,
      direction: state.direction,
      offset: state.offset,
      globalPropertySearch: state.globalPropertySearch,
    },
    productPropertyIds: state.propertyColumnIds?.filter(
      column => !SIMULATED_PROPERTY_COLUMNS.includes(column)
    ),
    productPropertyColumnIds: state.propertyColumnIds,
  };
};

export default ProductCollection;
